import { Component, OnDestroy, OnInit } from '@angular/core';
import { ColumnFilterComponent, FilterType } from '../shared/column-filter/column-filter.component';
import { MatchType } from '../shared/column-filter/column-filter-text-with-match-options-component';
import { ActivatedRoute, Router } from '@angular/router';
import { JhiEventManager, JhiLanguageService } from '../shared/core-utils';
import { AlertService } from '../shared/alert/alert.service';
import { finalize } from 'rxjs/internal/operators/finalize';
import { HttpResponse } from '@angular/common/http';
import { SORT_PREDICATE_NONE } from '../germplasm-manager/germplasm-search-resolve-paging-params';
import { GermplasmListService } from '../shared/germplasm-list/service/germplasm-list.service';
import { GermplasmListSearchRequest } from '../shared/germplasm-list/model/germplasm-list-search-request.model';
import { GermplasmListSearchResponse } from '../shared/germplasm-list/model/germplasm-list-search-response.model';
import { SearchResult } from '../shared/search-result.model';
import { ListType } from '../shared/list-builder/model/list-type.model';
import { ColumnFilterRadioButtonOption } from '../shared/column-filter/column-filter-radio-component';
import { MANAGE_GERMPLASM_LIST_PERMISSION } from '../shared/auth/permissions';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FeedbackService } from '../shared/feedback/service/feedback.service';
import { openSurvey } from '../shared/feedback/feedback-helper';
import { FeedbackFeatureEnum } from '../shared/feedback/feedback-feature.enum';
import { DropdownOptionData } from '../shared/column-filter/column-filter-dropdown-option-data';
import { SubscriptionsManager } from '../shared/util/subscriptions-manager';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'jhi-germplasm-list-search',
    templateUrl: './germplasm-list-search.component.html'
})
export class GermplasmListSearchComponent implements OnInit, OnDestroy {

    static readonly COLUMN_FILTER_EVENT_NAME = 'searchColumnFiltersChanged';

    IMPORT_GERMPLASM_LIST_PERMISSION = [...MANAGE_GERMPLASM_LIST_PERMISSION, 'IMPORT_GERMPLASM_LISTS'];
    ACTION_BUTTON_PERMISSIONS = [...MANAGE_GERMPLASM_LIST_PERMISSION, 'IMPORT_GERMPLASM_LISTS'];
    COLUMN_FILTER_EVENT_NAME = GermplasmListSearchComponent.COLUMN_FILTER_EVENT_NAME;

    itemsPerPage = 20;

    ColumnLabels = ColumnLabels;

    germplasmLists: GermplasmListSearchResponse[];
    searchRequest: GermplasmListSearchRequest;
    resultSearch: SearchResult;

    currentSearch: string;
    totalItems: number;
    page: number;
    previousPage: number;
    predicate: any;
    reverse: boolean;

    isLoading: boolean;

    germplasmListFilters: any;

    subsscriptionsManager = new SubscriptionsManager();

    constructor(private activatedRoute: ActivatedRoute,
                private jhiLanguageService: JhiLanguageService,
                private eventManager: JhiEventManager,
                private germplasmListService: GermplasmListService,
                private router: Router,
                private alertService: AlertService,
                private modalService: NgbModal,
                public translateService: TranslateService,
                private feedbackService: FeedbackService
    ) {
        this.page = 1;
        this.totalItems = 0;
        this.currentSearch = '';
        this.predicate = ColumnLabels.CREATION_DATE;
        this.reverse = false;
        this.resultSearch = new SearchResult('');
        this.searchRequest = new GermplasmListSearchRequest();
    }

    ngOnDestroy(): void {
        this.subsscriptionsManager.unsubscribeAll();
    }

    ngOnInit() {
        this.filters = this.getInitialFilters();
        this.eventManager.broadcast({ name: 'reloadFilters', content: { filters: this.filters, request: this.request } });
        this.registerColumnFiltersChanged();
        this.registerFilterBy();
        this.registerEvents();
        this.loadAll(this.request);
    }

    get request() {
        return this.searchRequest;
    }

    set request(request: GermplasmListSearchRequest) {
        this.searchRequest = request;
    }

    get filters() {
        return this.germplasmListFilters;
    }

    set filters(filters) {
        this.germplasmListFilters = filters;
    }

    loadAll(request: GermplasmListSearchRequest) {
        this.isLoading = true;
        this.germplasmListService.searchList(request,
            {
                page: this.page - 1,
                size: this.itemsPerPage,
                sort: this.getSort()
            }
        ).pipe(finalize(() => {
            this.isLoading = false;
        })).subscribe(
            (res: HttpResponse<GermplasmListSearchResponse[]>) => this.onSuccess(res.body, res.headers)
        );
    }

    loadPage(page: number) {
        if (page !== this.previousPage) {
            this.previousPage = page;
            this.transition();
        }
    }

    transition() {
        this.router.navigate(['./'], {
            queryParamsHandling: 'merge',
            queryParams: {
                page: this.page,
                size: this.itemsPerPage,
                search: this.currentSearch,
                sort: this.getSort()
            },
            relativeTo: this.activatedRoute
        });
        this.loadAll(this.request);
    }

    private getSort() {
        if (this.predicate === SORT_PREDICATE_NONE) {
            return '';
        }
        return [this.predicate + ',' + (this.reverse ? 'asc' : 'desc')];
    }

    private clearSort() {
        this.predicate = SORT_PREDICATE_NONE;
        this.reverse = false;
    }

    onClearSort($event) {
        $event.preventDefault();
        this.clearSort();
        this.transition();
    }

    trackId(index: number, item: GermplasmListSearchResponse) {
        return item.listId;
    }

    registerColumnFiltersChanged() {
        this.subsscriptionsManager.add = this.eventManager.subscribe(GermplasmListSearchComponent.COLUMN_FILTER_EVENT_NAME, (event) => {
            this.resetTable();
        });
    }

    registerFilterBy() {
        this.subsscriptionsManager.add = this.eventManager.subscribe('listNameFilter', (event) => {
            this.resetFilters();
            const listNameFilter = this.filters.filter((filter) => filter.type === FilterType.TEXT_WITH_MATCH_OPTIONS);
            listNameFilter[0].matchType = MatchType.EXACTMATCH;
            this.request.listNameFilter = event.content;
            this.eventManager.broadcast({ name: 'reloadFilters', content: { filters: this.filters, request: this.request } });
            this.request.listNameFilter = {
                type: MatchType.EXACTMATCH,
                value: event.content
            };
            this.resetTable();
        });
    }

    registerEvents() {
        this.subsscriptionsManager.add = this.eventManager.subscribe('addToGermplasmList', (event) => {
            this.alertService.success('germplasm-list.list-data.add-entries.success');
            this.resetTable();
        });

        this.subsscriptionsManager.add = this.eventManager.subscribe('germplasmListDeleted', (event) => {
            this.alertService.success('germplasm-list.list-data.delete-list.success');
            this.resetTable();
        });

        this.subsscriptionsManager.add = this.eventManager.subscribe('clonedGermplasmList', (event) => {
            this.resetTable();
        });

        this.subsscriptionsManager.add = this.eventManager.subscribe('listMetadataUpdated', (event) => {
            this.resetTable();
        });
    }

    private resetFilters() {
        this.filters = this.getInitialFilters();
        this.request = new GermplasmListSearchRequest();
        this.resultSearch = new SearchResult('');
    }

    resetTable() {
        this.page = 1;
        this.previousPage = 1;
        this.loadAll(this.request);
    }

    selectList($event, list: GermplasmListSearchResponse) {
        $event.preventDefault();

        this.router.navigate([], { skipLocationChange: true, queryParamsHandling: 'merge' }).then(() => {
            this.router.navigate([`/germplasm-list/list/${list.listId}`], {
                queryParams: { listId: list.listId, listName: list.listName },
                queryParamsHandling: 'merge'
            });
        });
    }

    private getInitialFilters() {
        return [
            {
                key: 'listNameFilter', name: 'column-filter.list.name', placeholder: 'column-filter.search.text.placeholder', type: FilterType.TEXT_WITH_MATCH_OPTIONS,
                matchType: MatchType.STARTSWITH, default: true
            },
            { key: 'parentFolderName', name: 'column-filter.parent.folder', placeholder: 'column-filter.contains.text.placeholder', type: FilterType.TEXT },
            { key: 'description', name: 'column-filter.description', placeholder: 'column-filter.contains.text.placeholder', type: FilterType.TEXT },
            { key: 'ownerName', name: 'column-filter.list.owner', placeholder: 'column-filter.contains.text.placeholder', type: FilterType.TEXT },
            {
                key: 'listTypes', name: 'column-filter.list.type', type: FilterType.DROPDOWN, values: this.getListTypesOptions(), multipleSelect: true,
                transform(req) {
                    ColumnFilterComponent.transformDropdownFilter(this, req);
                },
                reset(req) {
                    ColumnFilterComponent.resetDropdownFilter(this, req);
                },
            },
            { key: 'locked', name: 'column-filter.locked', type: FilterType.RADIOBUTTON, options: this.getStatusFilterOptions() },
            {
                key: 'numberOfEntriesRange', name: 'column-filter.number.of.entries.range', type: FilterType.NUMBER_RANGE,
                fromKey: 'numberOfEntriesFrom',
                toKey: 'numberOfEntriesTo',
                transform(req) {
                    ColumnFilterComponent.transformNumberRangeFilter(this, req, this.fromKey, this.toKey);
                },
                reset(req) {
                    ColumnFilterComponent.resetRangeFilter(this, req, this.fromKey, this.toKey);
                },
                reload(req) {
                    this.from = req[this.fromKey];
                    this.to = req[this.toKey];
                }
            },
            { key: 'notes', name: 'column-filter.notes', placeholder: 'column-filter.contains.text.placeholder', type: FilterType.TEXT },
            {
                key: 'listDate', name: 'column-filter.list.date', type: FilterType.DATE,
                fromKey: 'listDateFrom',
                toKey: 'listDateTo',
                transform(req) {
                    ColumnFilterComponent.transformDateFilter(this, req, this.fromKey, this.toKey);
                },
                reset(req) {
                    ColumnFilterComponent.resetRangeFilter(this, req, this.fromKey, this.toKey);
                },
                reload(req) {
                    this.from = req[this.fromKey];
                    this.to = req[this.toKey];
                }
            },
        ];
    }

    private getListTypesOptions(): Promise<DropdownOptionData[]> {
        return this.germplasmListService.getListTypes().toPromise().then((listTypes: ListType[]) => {
            return listTypes.filter((listType: ListType) => (listType.code !== 'FOLDER'))
                .map((listType: ListType) => {
                    return { id: listType.code, text: listType.name + ' (' + listType.code + ')' };
                });
        });
    }

    private getStatusFilterOptions(): Promise<ColumnFilterRadioButtonOption[]> {
        return new Promise<ColumnFilterRadioButtonOption[]>((resolve, reject) => {
            resolve([new ColumnFilterRadioButtonOption(true, 'yes'),
                new ColumnFilterRadioButtonOption(false, 'no')]);
        });
    }

    private onSuccess(data: GermplasmListSearchResponse[], headers) {
        this.totalItems = headers.get('X-Total-Count');
        this.germplasmLists = data;

        openSurvey(FeedbackFeatureEnum.GERMPLASM_LIST, this.feedbackService, this.modalService);
    }

}

export enum ColumnLabels {
    'LIST_NAME' = 'LIST_NAME',
    'PARENT_FOLDER' = 'PARENT_FOLDER_NAME',
    'DESCRIPTION' = 'DESCRIPTION',
    'LIST_OWNER' = 'LIST_OWNER',
    'LIST_TYPE' = 'LIST_TYPE',
    'NUMBER_OF_ENTRIES' = 'NUMBER_OF_ENTRIES',
    'LOCKED' = 'LOCKED',
    'NOTES' = 'NOTES',
    'CREATION_DATE' = 'CREATION_DATE'
}
